/**
 * The frame every report screen shares.
 *
 * Header (icon, title, live subtitle, view tabs, search, filters, columns,
 * export), body (empty / loading / table / analytics) and the pagination bar —
 * all the chrome that was previously copied per report. A report supplies its
 * filter drawer, its table and its analytics panel; everything else is here, so
 * a fix to the toolbar reaches every report at once.
 *
 * The toolbar's CONTROL / MENU classes are lifted verbatim from
 * ConversationInbox, which is why the reports read as the same product as the
 * inbox rather than merely similar to it.
 */
import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react'
import {
  Check, ChevronDown, ChevronLeft, ChevronRight, Columns3, GripVertical, Loader2,
  SlidersHorizontal, type LucideIcon,
} from 'lucide-react'

import ReportExportMenu from '../ReportExportMenu.js'
import ReportSearch, { useReportSearch } from '../ReportSearch.js'
import ReportViewTabs, { type ReportView } from '../analytics/ReportViewTabs.js'
import type { ReportColumnDef } from '../agent-performance/columns.js'
import Select from '../../ui/Select.js'

const R = 'rounded-[3px]'
const CONTROL = `flex h-8 items-center gap-2 ${R} border border-slate-200 bg-white px-2.5 text-[12.5px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900`
const CONTROL_ON = 'border-[#C9D9FB] bg-blue-50 text-[#3366FF] hover:border-[#C9D9FB] hover:text-[#3366FF]'
const MENU = `absolute top-full z-40 mt-1.5 overflow-hidden ${R} border border-slate-200 bg-white py-1 shadow-xl shadow-black/5`

export interface ReportShellProps<T> {
  Icon: LucideIcon
  title: string
  /** Live line under the title — row count, window, timezone. */
  subtitle: string
  /** True once filters have been applied — gates the toolbar and the body. */
  hasRun: boolean
  ranWith: unknown
  loading: boolean
  error: string | null
  rows: readonly T[]

  // --- columns (owned by the page's useReportView) ---
  catalog: ReportColumnDef[]
  visibleColumns: string[]
  onToggleColumn: (key: string) => void
  onReorderColumn: (from: string, to: string) => void
  onCommitColumns: () => void

  // --- search ---
  searchPlaceholder: string
  /** How a row's value for a column key is read, for the search filter. */
  readCell: (row: T, key: string) => unknown

  // --- export ---
  reportId: string
  reportName: string
  exportParams: Record<string, string> | null

  // --- content ---
  onOpenFilters: () => void
  renderTable: (pageRows: T[], visibleColumns: string[]) => ReactNode
  renderAnalytics: (rows: T[]) => ReactNode
  emptyTitle: string
  emptyDescription: string
  noResultsTitle: string
  noResultsDescription: string
  /** Extra toolbar controls, rendered ahead of the view tabs (e.g. Upload). */
  headerActions?: ReactNode
  /** The filter drawer, rendered by the page and owned by it. */
  children?: ReactNode
}

export default function ReportShell<T>({
  Icon,
  title,
  subtitle,
  hasRun,
  ranWith,
  loading,
  error,
  rows,
  catalog,
  visibleColumns,
  onToggleColumn,
  onReorderColumn,
  onCommitColumns,
  searchPlaceholder,
  readCell,
  reportId,
  reportName,
  exportParams,
  onOpenFilters,
  renderTable,
  renderAnalytics,
  emptyTitle,
  emptyDescription,
  noResultsTitle,
  noResultsDescription,
  headerActions,
  children,
}: ReportShellProps<T>): JSX.Element {
  const [columnMenuOpen, setColumnMenuOpen] = useState(false)
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState('')
  const [view, setView] = useState<ReportView>('table')
  const columnMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!columnMenuOpen) return
    const onDown = (e: MouseEvent): void => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target as Node)) {
        setColumnMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [columnMenuOpen])

  // A fresh run must land on page 1 — page 7 of the previous result set is a
  // blank table for the new one.
  useEffect(() => { setPage(1) }, [ranWith])

  // Paged client-side: the window is already bounded by the filter drawer, so
  // the whole result set is in hand and re-querying per page would be a round
  // trip for data the browser already has.
  const visible = useReportSearch(rows as T[], search, visibleColumns, readCell)
  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const pageRows = visible.slice((safePage - 1) * pageSize, safePage * pageSize)
  const hiddenColumns = catalog.length - visibleColumns.length

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <header className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-slate-200 bg-white px-5 py-3">
        <div className="flex shrink-0 items-center gap-3">
          <span className={`flex h-10 w-10 items-center justify-center border border-blue-200 ${R} bg-blue-50 text-[#3366FF]`}>
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-lg font-bold leading-tight tracking-tight text-slate-900">
              {title}
            </h1>
            <p className="text-[12px] leading-tight text-slate-400">{subtitle}</p>
          </div>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {headerActions}
          <ReportViewTabs value={view} onChange={setView} disabled={!hasRun} />

          <ReportSearch
            value={search}
            onChange={(v) => { setSearch(v); setPage(1) }}
            disabled={!hasRun || rows.length === 0}
            placeholder={searchPlaceholder}
          />

          <button
            onClick={onOpenFilters}
            className={`${CONTROL} ${ranWith ? CONTROL_ON : ''}`}
            title="Report filters"
          >
            <SlidersHorizontal className={`h-4 w-4 ${ranWith ? 'text-[#3366FF]' : 'text-slate-400'}`} />
            Filters
          </button>

          {/* Column choice belongs to the table; the analytics view has no columns. */}
          <div ref={columnMenuRef} className={`relative ${view === 'analytics' ? 'hidden' : ''}`}>
            <button
              onClick={() => setColumnMenuOpen((v) => !v)}
              className={`${CONTROL} ${hiddenColumns > 0 ? CONTROL_ON : ''}`}
              title="Choose columns"
            >
              <Columns3 className={`h-4 w-4 ${hiddenColumns > 0 ? 'text-[#3366FF]' : 'text-slate-400'}`} />
              Columns
              {hiddenColumns > 0 && (
                <span className="text-[11px] font-bold tabular-nums">
                  {visibleColumns.length}/{catalog.length}
                </span>
              )}
              <ChevronDown
                className={`h-3.5 w-3.5 text-slate-400 transition-transform ${columnMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {columnMenuOpen && (
              <div className={`${MENU} right-0 max-h-[420px] w-64 overflow-y-auto`}>
                <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
                  Drag to reorder · saved to your profile
                </p>
                {catalog.map((col) => {
                  const checked = visibleColumns.includes(col.key)
                  return (
                    <div
                      key={col.key}
                      draggable={!col.locked}
                      onDragStart={() => setDragKey(col.key)}
                      onDragOver={(e) => {
                        e.preventDefault()
                        if (dragKey && dragKey !== col.key) onReorderColumn(dragKey, col.key)
                      }}
                      onDragEnd={() => { setDragKey(null); onCommitColumns() }}
                      onClick={() => onToggleColumn(col.key)}
                      className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12.5px] font-medium transition-colors hover:bg-slate-50 ${
                        dragKey === col.key ? 'opacity-50' : ''
                      }`}
                    >
                      <GripVertical
                        className={`h-3.5 w-3.5 shrink-0 ${col.locked ? 'text-slate-200' : 'text-slate-300'}`}
                      />
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center ${R} border ${
                          checked ? 'border-[#3366FF] bg-[#3366FF]' : 'border-slate-300 bg-white'
                        }`}
                      >
                        {checked && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span className={`truncate ${col.locked ? 'text-slate-400' : 'text-slate-700'}`}>
                        {col.label}
                      </span>
                      {col.locked && (
                        <span className="ml-auto text-[10px] font-semibold uppercase text-slate-300">
                          Pinned
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <ReportExportMenu
            reportId={reportId}
            reportName={reportName}
            params={exportParams}
            columns={visibleColumns}
            disabled={!hasRun || rows.length === 0}
            controlClass={CONTROL}
            menuClass={MENU}
          />
        </div>
      </header>

      {error && (
        <div role="alert" className="border-b border-red-100 bg-red-50 px-5 py-2.5 text-[12.5px] text-red-700">
          {error}
        </div>
      )}

      <div className="scrollbar-thin relative flex-1 overflow-auto bg-white">
        {loading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
            <Loader2 className="h-6 w-6 animate-spin text-[#3366FF]" />
          </div>
        )}

        {!hasRun && !loading ? (
          <EmptyState
            Icon={Icon}
            title={emptyTitle}
            description={emptyDescription}
            actionLabel="Open filters"
            onAction={onOpenFilters}
          />
        ) : hasRun && visible.length === 0 && !loading && !error ? (
          <EmptyState
            Icon={Icon}
            title={search ? 'No matching rows' : noResultsTitle}
            description={search
              ? 'No row matches your search. Clear it, or adjust the filters.'
              : noResultsDescription}
            actionLabel="Change filters"
            onAction={onOpenFilters}
          />
        ) : view === 'analytics' ? (
          renderAnalytics(visible)
        ) : (
          renderTable(pageRows, visibleColumns)
        )}
      </div>

      {hasRun && visible.length > 0 && view === 'table' && (
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 bg-white px-5 py-2.5">
          <div className="flex items-center gap-2 text-[12px] text-slate-500">
            <span>Rows per page</span>
            <Select
              ariaLabel="Rows per page"
              size="sm"
              value={String(pageSize)}
              onChange={(v) => { setPageSize(Number(v)); setPage(1) }}
              options={[25, 50, 100, 200].map((n) => ({ value: String(n), label: String(n) }))}
              className="w-[72px]"
            />
            <span className="ml-2 font-mono tabular-nums">
              {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, visible.length)} of {visible.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((n) => Math.max(1, n - 1))}
              disabled={safePage <= 1}
              className={`flex h-8 w-8 items-center justify-center ${R} border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40`}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 font-mono text-[12px] font-semibold tabular-nums text-slate-600">
              {safePage} / {totalPages}
            </span>
            <button
              onClick={() => setPage((n) => Math.min(totalPages, n + 1))}
              disabled={safePage >= totalPages}
              className={`flex h-8 w-8 items-center justify-center ${R} border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40`}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {children}
    </div>
  )
}

function EmptyState({
  Icon,
  title,
  description,
  actionLabel,
  onAction,
}: {
  Icon: LucideIcon
  title: string
  description: string
  actionLabel: string
  onAction: () => void
}): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-16 text-center">
      <span className={`mb-3 flex h-12 w-12 items-center justify-center border border-blue-200 ${R} bg-blue-50`}>
        <Icon className="h-5 w-5 text-[#3366FF]" aria-hidden="true" />
      </span>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-xs text-slate-500">{description}</p>
      <button
        onClick={onAction}
        className={`mt-4 flex h-8 items-center gap-2 ${R} bg-[#3366FF] px-4 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2952CC]`}
      >
        <SlidersHorizontal className="h-4 w-4" />
        {actionLabel}
      </button>
    </div>
  )
}
